/*
 * <dwm-slider> — progressive enhancement for a native scroll-snap track.
 *
 * The track scrolls on its own (swipe, trackpad, shift + wheel). This element only adds:
 *   - previous / next buttons (they appear once this script runs);
 *   - a counter and an `is-active` slide: the slide nearest to the start of the track;
 *   - clicking a partly visible slide scrolls it into place;
 *   - a slide that receives keyboard focus is scrolled into place, so it is always fully visible.
 *
 * One source of truth: the track's scroll position. Nothing else is stored. Smooth scrolling only when motion is
 * allowed. All listeners are removed on disconnect.
 */
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

class DwmSlider extends HTMLElement {
  #track = null;
  #slides = [];
  #prev = null;
  #next = null;
  #current = null;
  #frame = 0;
  #index = -1;

  connectedCallback() {
    this.#track = this.querySelector('[data-slider-track]');
    this.#slides = [...this.querySelectorAll('[data-slider-slide]')];
    this.#prev = this.querySelector('[data-slider-prev]');
    this.#next = this.querySelector('[data-slider-next]');
    this.#current = this.querySelector('[data-slider-current]');

    if (!this.#track || this.#slides.length < 2) {
      return;
    }

    this.querySelector('[data-slider-controls]')?.removeAttribute('hidden');
    this.classList.add('is-ready');

    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#track.addEventListener('click', this.#onSlideClick);
    this.#track.addEventListener('focusin', this.#onFocusIn);
    this.#prev?.addEventListener('click', this.#onPrev);
    this.#next?.addEventListener('click', this.#onNext);
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#frame);
    this.#track?.removeEventListener('scroll', this.#onScroll);
    this.#track?.removeEventListener('click', this.#onSlideClick);
    this.#track?.removeEventListener('focusin', this.#onFocusIn);
    this.#prev?.removeEventListener('click', this.#onPrev);
    this.#next?.removeEventListener('click', this.#onNext);
    window.removeEventListener('resize', this.#onScroll);
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  /* Previous / next follow the scroll position itself: the nearest slide start before or after where the track is
     now. (Near the end several slides can share the last scroll position, so stepping by index could stall.) */
  #onPrev = () => {
    const left = this.#track.scrollLeft;
    const index = this.#slides.findLastIndex((slide) => this.#offset(slide) < left - 2);
    this.goTo(Math.max(0, index));
  };

  #onNext = () => {
    const left = this.#track.scrollLeft;
    const index = this.#slides.findIndex((slide) => this.#offset(slide) > left + 2);
    this.goTo(index < 0 ? this.#slides.length - 1 : index);
  };

  #onSlideClick = (event) => {
    const slide = event.target.closest('[data-slider-slide]');
    const index = this.#slides.indexOf(slide);

    if (index >= 0 && index !== this.#index) {
      this.goTo(index);
    }
  };

  #onFocusIn = (event) => {
    const slide = event.target.closest('[data-slider-slide]');
    const index = this.#slides.indexOf(slide);

    if (index >= 0) {
      this.goTo(index);
    }
  };

  /* Where the first slide starts inside the track (the inset in line with the page container). Read from the layout,
     because the scroll-padding is a calc() with percentages that getComputedStyle does not resolve to pixels. */
  #inset() {
    return this.#slides[0].offsetLeft - this.#track.offsetLeft;
  }

  /* The scroll position at which a slide starts where the first slide starts. */
  #offset(slide) {
    return slide.offsetLeft - this.#slides[0].offsetLeft;
  }

  /* Scroll so the slide starts where the first slide starts (in line with the page container). */
  goTo(index) {
    const target = this.#slides[Math.max(0, Math.min(index, this.#slides.length - 1))];

    this.#track.scrollTo({
      left: this.#offset(target),
      behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
    });
  }

  /* Derive the active slide from the scroll position only. */
  #update() {
    const start = this.#track.getBoundingClientRect().left + this.#inset();
    const maxScroll = this.#track.scrollWidth - this.#track.clientWidth;
    let index = 0;
    let best = Infinity;

    this.#slides.forEach((slide, i) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - start);

      if (distance < best) {
        best = distance;
        index = i;
      }
    });

    // At the very end of the track the last slide is the active one, even if it cannot reach the start position.
    if (this.#track.scrollLeft >= maxScroll - 2) {
      index = this.#slides.length - 1;
    }

    if (index !== this.#index) {
      this.#index = index;
      this.#slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
      });

      if (this.#current) {
        this.#current.textContent = String(index + 1).padStart(2, '0');
      }
    }

    this.#prev?.setAttribute('aria-disabled', String(this.#track.scrollLeft <= 2));
    this.#next?.setAttribute('aria-disabled', String(this.#track.scrollLeft >= maxScroll - 2));
  }
}

if (!customElements.get('dwm-slider')) {
  customElements.define('dwm-slider', DwmSlider);
}
